import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useABTest } from '../../context/ABTestContext'
import { useClient } from '../../context/ClientContext'

/**
 * GuidedTour
 * Custom guided tour — no external library dependency (React 19 compatible).
 * Renders a spotlight overlay + positioned tooltip for each step.
 * Completely locked: overlay click and Escape are disabled.
 *
 * Props:
 *   isProfileLoading {Boolean} - Prevents tour starting before DOM targets are ready
 */
const GuidedTour = ({ isProfileLoading }) => {
  const { tourActive, activeTourAssignment, completeTour } = useABTest()
  const { client } = useClient()
  const navigate = useNavigate()

  const [currentStep, setCurrentStep] = useState(0)
  const [targetRect, setTargetRect] = useState(null)
  const [targetRetry, setTargetRetry] = useState(0)
  const tourSteps = activeTourAssignment?.content?.tour_steps || []
  const step = tourSteps[currentStep]
  const isCentered = !step || step.target === 'center'
  const showCenteredFallback = !isCentered && !targetRect && targetRetry >= 10
  const isLastStep = currentStep === tourSteps.length - 1

  // Reset step index whenever a new tour starts
  useEffect(() => {
    if (tourActive) setCurrentStep(0)
  }, [tourActive])

  useEffect(() => {
    setTargetRetry(0)
    setTargetRect(null)
  }, [tourActive, currentStep])

  // Measure target element position and scroll it into view
  const measureTarget = useCallback(() => {
    if (!step || isCentered) {
      setTargetRect(null)
      return true
    }
    const el = document.querySelector(`[data-tour="${step.target}"]`)
    if (!el) {
      setTargetRect(null)
      return false
    }
    // Temporarily release scroll lock so programmatic scroll works
    document.body.style.overflow = ''
    const absoluteTop = el.getBoundingClientRect().top + window.scrollY - window.innerHeight / 2 + el.offsetHeight / 2
    window.scrollTo({ top: Math.max(0, absoluteTop), behavior: 'smooth' })
    // Re-lock after scroll settles, then measure
    setTimeout(() => {
      document.body.style.overflow = 'hidden'
      const rect = el.getBoundingClientRect()
      setTargetRect({ ...rect.toJSON() })
    }, 400)
    return true
  }, [step, isCentered])

  useEffect(() => {
    if (!tourActive || isProfileLoading) return
    const measured = measureTarget()
    if (!measured && targetRetry < 10) {
      const retryTimer = setTimeout(() => {
        setTargetRetry(prev => prev + 1)
      }, 200)
      return () => clearTimeout(retryTimer)
    }
  }, [tourActive, currentStep, isProfileLoading, measureTarget, targetRetry])

  // Re-measure on window resize
  useEffect(() => {
    if (!tourActive) return
    window.addEventListener('resize', measureTarget)
    return () => window.removeEventListener('resize', measureTarget)
  }, [tourActive, measureTarget])

  // Block Escape key while tour is active
  useEffect(() => {
    if (!tourActive) return
    const blockEscape = (e) => { if (e.key === 'Escape') e.preventDefault() }
    document.addEventListener('keydown', blockEscape, true)
    return () => document.removeEventListener('keydown', blockEscape, true)
  }, [tourActive])

  // Lock body scroll while tour is active
  useEffect(() => {
    if (!tourActive) {
      document.body.style.overflow = ''
      return
    }
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [tourActive])

  const handleNext = useCallback(async () => {
    if (!isLastStep) {
      setCurrentStep(prev => prev + 1)
    } else {
      await completeTour(activeTourAssignment)
      navigate(`/${client?.tenantUrlName}/`)
    }
  }, [isLastStep, completeTour, activeTourAssignment, navigate, client?.tenantUrlName])

  if (!tourActive || !activeTourAssignment || isProfileLoading || tourSteps.length === 0) {
    return null
  }

  const PAD = 6 // spotlight padding around target
  const TOOLTIP_WIDTH = 320
  const CIRCLE_SIZE = 36

  /**
   * Shared positioning calculations for arrow + step number circle.
   * Returns { isBelow, arrowLeft } or null if no target.
   */
  const getArrowInfo = () => {
    if (!targetRect) return null
    const spaceBelow = window.innerHeight - targetRect.bottom
    const isBelow = spaceBelow > 200
    const tooltipLeft = Math.max(16, Math.min(targetRect.left, window.innerWidth - 352))
    const targetCenterX = targetRect.left + targetRect.width / 2
    const arrowLeft = Math.max(CIRCLE_SIZE / 2 + 4, Math.min(targetCenterX - tooltipLeft, TOOLTIP_WIDTH - CIRCLE_SIZE / 2 - 4))
    return { isBelow, arrowLeft }
  }

  /** White CSS-triangle arrow pointing from tooltip toward the target */
  const getArrowStyle = () => {
    const info = getArrowInfo()
    if (!info) return null
    const { isBelow, arrowLeft } = info
    const base = {
      position: 'absolute',
      left: arrowLeft,
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '9px solid transparent',
      borderRight: '9px solid transparent',
    }
    return isBelow
      ? { ...base, top: -9, borderBottom: '9px solid #fff' }
      : { ...base, bottom: -9, borderTop: '9px solid #fff' }
  }

  /**
   * Primary color circle with step number, positioned just outside the
   * tooltip — between the arrow tip and the highlighted element.
   */
  const getCircleStyle = () => {
    const info = getArrowInfo()
    if (!info) return null
    const { isBelow, arrowLeft } = info
    const base = {
      position: 'absolute',
      left: arrowLeft,
      transform: 'translateX(-50%)',
      width: CIRCLE_SIZE,
      height: CIRCLE_SIZE,
      borderRadius: '50%',
      background: 'hsl(var(--primary))',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 13,
      fontWeight: 700,
      border: '2px solid hsl(var(--primary) / 0.5)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    }
    // Arrow is 9px tall; place circle beyond the arrow tip with a 4px gap
    return isBelow
      ? { ...base, top: -(9 + CIRCLE_SIZE + 4) }
      : { ...base, bottom: -(9 + CIRCLE_SIZE + 4) }
  }

  // Tooltip position: prefer below the target, fall back to above if near bottom
  const getTooltipStyle = () => {
    if (!targetRect) return {}
    const info = getArrowInfo()
    const isBelow = info?.isBelow ?? true
    // Extra offset to leave room for the circle (CIRCLE_SIZE + 4px gap + arrow 9px)
    const circleOffset = CIRCLE_SIZE + 4 + 9
    const top = isBelow
      ? targetRect.bottom + circleOffset + 4
      : targetRect.top - circleOffset - 4
    const left = Math.max(16, Math.min(targetRect.left, window.innerWidth - 352))
    return { top, left, transform: isBelow ? 'none' : 'translateY(-100%)' }
  }

  return createPortal(
    <>
      {/* ── Dark overlay (locked — blocks interaction) ── */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
        onClick={(e) => e.stopPropagation()}
      />

      {isCentered || showCenteredFallback ? (
        /* ── Centered modal step (no spotlight) ── */
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.55)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: '28px 24px',
              maxWidth: 360,
              width: 'calc(100vw - 32px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            }}
          >
            <TooltipContent
              step={step}
              onNext={handleNext}
              isLast={isLastStep}
              currentStep={currentStep}
              totalSteps={tourSteps.length}
              isMain
            />
          </div>
        </div>
      ) : (
        /* ── Spotlight + anchored tooltip ── */
        <>
          {/* Darkened full-screen backdrop */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.55)' }}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Spotlight cutout — sits above backdrop, punches a hole via box-shadow */}
          {targetRect && (
            <div
              style={{
                position: 'fixed',
                top: targetRect.top - PAD,
                left: targetRect.left - PAD,
                width: targetRect.width + PAD * 2,
                height: targetRect.height + PAD * 2,
                borderRadius: 12,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.55), 0 0 0 2px rgba(255,255,255,0.5), 0 0 20px 8px rgba(255,255,255,0.12)',
                zIndex: 10000,
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Tooltip anchored to target */}
          {targetRect && (
            <div
              style={{
                position: 'fixed',
                ...getTooltipStyle(),
                zIndex: 10001,
                background: '#fff',
                borderRadius: 16,
                padding: '20px',
                width: TOOLTIP_WIDTH,
                maxWidth: 'calc(100vw - 32px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              }}
            >
              {/* White triangle arrow pointing toward the target element */}
              {getArrowStyle() && <div style={getArrowStyle()} />}

              {/* Primary circle with step number, outside the arrow toward the target */}
              {getCircleStyle() && (
                <div style={getCircleStyle()}>
                  {currentStep + 1}
                </div>
              )}

              <TooltipContent
                step={step}
                onNext={handleNext}
                isLast={isLastStep}
                currentStep={currentStep}
                totalSteps={tourSteps.length}
              />
            </div>
          )}
        </>
      )}
    </>,
    document.body
  )
}

/** Shared tooltip body used by both centered and anchored layouts */
const TooltipContent = ({ step, onNext, isLast, currentStep, totalSteps, isMain }) => (
  <>
    {/* Title */}
    {step.title && (
      <p style={{ fontSize: 12, fontWeight: 700, color: isMain ? '#000' : 'hsl(var(--primary))', margin: '0 0 6px' }}>
        {step.title}
      </p>
    )}

    <p style={{ fontSize: 11, color: 'hsl(var(--secondary))', lineHeight: 1.6, margin: '0 0 12px' }}>
      {step.body}
    </p>

    {/* Bottom row: button left, step count bottom-right */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <button
        onClick={onNext}
        style={{
          padding: '6px 18px',
          borderRadius: 8,
          border: isLast ? 'none' : '2px solid hsl(var(--primary))',
          background: isLast ? 'hsl(var(--primary))' : 'transparent',
          color: isLast ? '#fff' : 'hsl(var(--primary))',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          letterSpacing: '0.01em',
        }}
      >
        {step.button}
      </button>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', whiteSpace: 'nowrap' }}>
        {currentStep + 1}/{totalSteps}
      </span>
    </div>
  </>
)

export default GuidedTour
